import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import LazyVideo from "../components/LazyVideo";
import { VIDEOS, POSTERS } from "../lib/media";

interface CityTileProps { video: string; poster?: string; tint: string; tag: string; city: string; }
function CityTile({ video, poster, tint, tag, city }: CityTileProps) {
  return (
    <div className="liquid-glass rounded-2xl overflow-hidden relative group min-h-[140px] md:min-h-[180px]">
      <LazyVideo className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-[1500ms] group-hover:scale-110"
             src={video} poster={poster} />
      <div className={`absolute inset-0 bg-gradient-to-b ${tint} mix-blend-multiply pointer-events-none`} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/40 pointer-events-none" />
      <div className="relative h-full p-4 flex flex-col justify-between">
        <div className="text-white/70 text-[10px] tracking-widest uppercase">{tag}</div>
        <div className="font-instrument text-2xl text-white tracking-tight">{city}</div>
      </div>
    </div>
  );
}

interface IndustryTileProps { n: string; title: string; desc: string; href: string; delay?: number; video?: string; poster?: string; }
function IndustryTile({ n, title, desc, href, delay = 0, video, poster }: IndustryTileProps) {
  return (
    <Reveal delay={delay}>
      <article className="liquid-glass rounded-3xl p-6 md:p-8 h-full flex flex-col justify-between min-h-[220px] group hover:bg-white/[0.02] transition-colors overflow-hidden">
        {video && (
          <>
            <LazyVideo className="absolute inset-0 w-full h-full object-cover opacity-40 transition-transform duration-[1500ms] group-hover:scale-105"
                   src={video} poster={poster} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10 pointer-events-none" />
          </>
        )}
        <div className="flex items-start justify-between relative z-10">
          <div className="text-white/40 text-xs tracking-widest uppercase">{n}</div>
          <a href={href} className="text-white/40 group-hover:text-white transition-colors" aria-label={title}><ArrowUpRight size={16} /></a>
        </div>
        <div className="mt-12 relative z-10">
          <h4 className="font-instrument text-white text-2xl md:text-3xl tracking-tight">{title}</h4>
          <p className="text-white/55 text-sm leading-relaxed mt-3 max-w-md">{desc}</p>
        </div>
      </article>
    </Reveal>
  );
}

const TILES = [
  { n: "01 / Mining",       title: "Mining & Resources",           video: VIDEOS.mining,       poster: POSTERS.mining,       href: "/industries/mining.html",        desc: "Predictive maintenance, drone & vision QA, AI dispatch, ore-grade prediction. Built for the realities of WA fleet operations." },
  { n: "02 / Machinery",    title: "Heavy Machinery",              video: VIDEOS.machinery,    poster: POSTERS.machinery,    href: "/industries/machinery.html",     desc: "Sensor fusion, telematics, anomaly detection and operator-facing copilots that cut downtime and extend asset life." },
  { n: "03 / GIS",          title: "GIS & Geospatial",             video: VIDEOS.gis,          poster: POSTERS.gis,          href: "/industries/gis.html",           desc: "AI-augmented geospatial: change detection from satellite, semantic search across map layers, automated cadastral reconciliation." },
  { n: "04 / Construction", title: "Construction & Infrastructure", video: VIDEOS.construction, poster: POSTERS.construction, href: "/industries/construction.html",  desc: "AI-powered site copilots, compliance evidence packs and supply-chain transparency tools for tier-1 builders and infrastructure delivery teams." },
];

const CITIES = [
  { city: "Auckland",   tag: "HQ · New Zealand",  video: VIDEOS.auckland,  poster: POSTERS.auckland,  tint: "from-emerald-400/30 via-teal-700/25 to-slate-950/80" },
  { city: "Perth (WA)", tag: "Mining hub",         video: VIDEOS.perth,     poster: POSTERS.perth,     tint: "from-amber-400/35 via-orange-700/25 to-slate-950/85" },
  { city: "Sydney",     tag: "Defence & finance",  video: VIDEOS.sydney,    poster: POSTERS.sydney,    tint: "from-sky-400/30 via-blue-700/25 to-slate-950/85" },
  { city: "Worldwide",    tag: "Global delivery",    video: VIDEOS.defence,    poster: POSTERS.defence,    tint: "from-fuchsia-400/30 via-indigo-700/25 to-slate-950/85" },
];

export default function Industries() {
  return (
    <section id="industries" className="bg-black py-28 md:py-40 px-6 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <Reveal><div className="text-white/40 text-sm tracking-widest uppercase">Industries</div></Reveal>
        <Reveal delay={100}>
          <h2 className="font-instrument text-5xl md:text-7xl lg:text-8xl text-white tracking-tight mt-6 leading-[1.05]">
            Built for the sectors that <em className="italic-serif text-white/50">move</em> the country.
          </h2>
        </Reveal>
        <Reveal delay={220}>
          <p className="mt-8 text-white/60 text-base md:text-lg leading-relaxed max-w-2xl">
            We don't dabble. Our teams have shipped production systems into mines outside Perth, machinery workshops in Auckland, GIS desks in Canberra and analytics units across the Tasman.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-16">
          {TILES.map((t, i) => <IndustryTile key={t.title} n={t.n} title={t.title} desc={t.desc} href={t.href} delay={i * 100} video={t.video} poster={t.poster} />)}
        </div>

        <Reveal delay={200}>
          <div className="liquid-glass rounded-3xl p-6 md:p-8 mt-8">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
              <h3 className="font-instrument text-2xl md:text-3xl text-white tracking-tight">Where we work</h3>
              <div className="text-white/40 text-xs tracking-widest uppercase">HQ in NZ · Delivery: AU · NZ · Worldwide</div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {CITIES.map((c) => <CityTile key={c.city} {...c} />)}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
